/**
 * components/Auth/LoginModal.tsx - Authentication Modal Component
 * 
 * This component provides:
 * - User login functionality with phone number authentication
 * - Development admin accounts with hardcoded credentials
 * - Forgot password options
 * - Form validation for phone numbers
 * - Modal overlay with backdrop click to close
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import styles from './LoginModal.module.css';

// Permission type for user payloads
type Permission = 'dashboard' | 'vendors' | 'users' | 'events' | 'orders' | 'analytics';

interface User {
  id: string;
  phoneNumber: string;
  password?: string;
  name: string;
  role: string;
  email: string;
  status?: string;
  isAdmin: boolean;
  permissions?: Permission[];
  lastLogin?: string;
  createdAt?: string;
  vendorId?: string | null;
}

interface LoginFormData {
  username: string;
  phoneNumber: string;
  password: string;
  email: string;
}

interface FormErrors {
  general?: string;
  username?: string;
  phoneNumber?: string;
  password?: string;
  email?: string;
}

interface LoginModalProps {
  onClose: () => void;
  onLogin: (user: User) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onClose, onLogin }) => {
  const navigate = useNavigate();
  const [formMode, setFormMode] = useState<'login' | 'forgotPassword'>('login');
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    phoneNumber: '',
    password: '',
    email: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (formMode === 'login') {
      if (!formData.username.trim() && !formData.phoneNumber.trim()) {
        newErrors.general = 'Username or phone number is required';
      }
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      }
    } else if (formMode === 'forgotPassword') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      if (formMode === 'login') {
        // Call API authentication endpoint
        const response = await authService.login(
          formData.username || formData.phoneNumber, 
          formData.password
        );
        
        // Store user data in localStorage (for UI purposes)
        const userData = {
          id: response.user.id,
          name: response.user.name,
          username: response.user.username,
          role: response.user.role,
          email: response.user.email,
          isAdmin: response.user.role === 'super-admin' || response.user.role === 'admin',
          permissions: response.user.permissions as Permission[],
          vendorId: response.user.vendorId || null
        };
        localStorage.setItem('oni_current_user', JSON.stringify(userData));

        // Dispatch custom event to notify other components of login
        window.dispatchEvent(new CustomEvent('authLogin', { detail: { token: response.token } }));

        // Redirect to admin dashboard
        navigate('/admin');
      } else if (formMode === 'forgotPassword') {
        // TODO: Implement forgot password API call
        // For now, show placeholder message
        setSuccessMessage('Password reset functionality will be implemented with backend API.');
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrors({ general: error.message });
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    }

    setIsLoading(false);
  };

  const resetForm = () => {
    setFormData({ username: '', phoneNumber: '', password: '', email: '' });
    setErrors({});
    setSuccessMessage('');
  };

  const switchMode = (mode: 'login' | 'forgotPassword') => {
    setFormMode(mode);
    resetForm();
  };

  const renderLoginForm = () => {
    return (
      <>
        <h2 className={styles.title}>Welcome back</h2>
        <p className={styles.subtitle}>Sign in to your admin dashboard</p>

        {errors.general && (
          <div className={styles.errorMessage}>
            <p>{errors.general}</p>
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Username
          </label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            placeholder="Enter your username"
            className={`${styles.input} ${errors.username ? styles.inputError : ''}`}
          />
          {errors.username && (
            <p className={styles.fieldError}>{errors.username}</p>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Password</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Enter your password"
            className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
          />
          {errors.password && (
            <p className={styles.fieldError}>{errors.password}</p>
          )}
        </div>

        <div className={styles.forgotLinks}>
          <button
            type="button"
            onClick={() => switchMode('forgotPassword')}
            className={styles.linkButton}
          >
            Forgot password?
          </button>
        </div>
      </>
    );
  };

  const renderForgotForm = () => (
    <>
      <h2 className={styles.title}>
        Reset Password
      </h2>
      <p className={styles.subtitle}>
        Enter your email to receive password reset instructions
      </p>

      {successMessage && (
        <div className={styles.successMessage}>
          <p>{successMessage}</p>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Email Address
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Enter your email address"
          className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
        />
        {errors.email && (
          <p className={styles.fieldError}>{errors.email}</p>
        )}
      </div>

      <div className={styles.backLink}>
        <button
          type="button"
          onClick={() => switchMode('login')}
          className={styles.linkButton}
        >
          ← Back to sign in
        </button>
      </div>
    </>
  );

  return (
    <div className={styles.loginPage}>
      {/* Left brand panel */}
      <div className={styles.leftPanel}>
        <div className={styles.brandLogo}>
          <div className={styles.brandIcon}>⚡</div>
          <span className={styles.brandName}>ONI</span>
        </div>
        <div className={styles.brandBody}>
          <h1 className={styles.brandHeadline}>Stadium Express</h1>
          <p className={styles.brandSub}>Managing the experience behind every seat.</p>
        </div>
        <p className={styles.brandFooter}>Optimal Network Integrations</p>
      </div>

      {/* Right form panel */}
      <div className={styles.rightPanel}>
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} className={styles.form}>
            {formMode === 'login' ? renderLoginForm() : renderForgotForm()}

            <button
              type="submit"
              disabled={isLoading}
              className={`${styles.submitButton} ${isLoading ? styles.submitButtonLoading : ''}`}
            >
              {isLoading ? 'Signing in…' :
               formMode === 'login' ? 'Sign In' :
               'Send Reset Instructions'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;